import { ChangeEventHandler, Fragment, FunctionComponent, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  DraftConfigurationSpecialTextStyle,
  DraftConfigurationTextStyle,
  DraftConfigurationWithTextOrSpecialText,
} from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { isMultiLine } from '@/utils/text/isMultiLine';
import { useConfigSwitch } from '@/hooks/useConfigSwitch';
import { useDebounce } from '@/hooks/useDebounce';
import {
  isDraftPlaceholderTextConfiguration,
  isDraftSpecialTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { getPrintAreaLabel } from '@/model/printArea/getPrintAreaLabel';
import { setConfigurationText } from '@/redux/draft/actions/setConfigurationText';
import {
  setConfigurationSpecialTextStyle,
  setConfigurationTextStyle,
} from '@/redux/draft/actions/setConfigurationTextStyle';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { StoreDispatch } from '@/redux/types';
import { FontList } from '@/components/common/FontList';
import { PlaceholderControls } from '@/components/common/PlaceholderControls';
import { PrintAreaSelector } from '@/components/common/PrintAreaSelector';
import { useFocus } from '@/components/common/focus';
import { Panel, SubPanel } from '@/components/common/panel/panel';
import { FontSize } from '@/components/common/text-configuration/FontSize';
import { TextAngle } from '@/components/common/text-configuration/TextAngle';
import { setPlaceholderWidthAction } from '@/components/draft/draftState/actions/setPlaceholderWidthAction';
import { setTextAngleAction } from '@/components/draft/draftState/actions/setTextAngleAction';
import { ConfigurationControls } from '../ConfigurationControls';
import { ColorList } from './ColorList';
import { FontSelector } from './FontSelector';
import { TextControls } from './TextControls';
import './TextPanel.scss';

interface Props {
  configuration: DraftConfigurationWithTextOrSpecialText;
}

enum SubPanels {
  None,
  FontList,
  ColorList,
  PrintArea,
}

export const TextPanel: FunctionComponent<Props> = ({ configuration }) => {
  const {
    text: {
      content: textContent,
      style: { font, textAlign },
    },
  } = configuration;
  const [text, setText] = useState<string>(textContent);
  const [activeSubPanel, setActiveSubPanel] = useState(SubPanels.None);
  const textarea = useRef<HTMLTextAreaElement | null>(null);
  const dispatch = useDispatch<StoreDispatch>();
  const draftDispatch = useReduxDraftDispatch();
  const debouncedTextUpdate = useDebounce(
    (cid: number, text: string) => dispatch(setConfigurationText(cid, text)),
    300
  );
  const [textPanel, focusTextPanel] = useFocus();

  useConfigSwitch(configuration, () => {
    focusTextPanel();
    setText(textContent);
  });

  const handleInputChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setText(e.target.value);
    debouncedTextUpdate(configuration.cid, e.target.value);
  };

  function handleBackClick() {
    setActiveSubPanel(SubPanels.None);
  }

  function handleStyleChange(
    style: DraftConfigurationTextStyle | DraftConfigurationSpecialTextStyle
  ) {
    if (isDraftSpecialTextConfiguration(configuration)) {
      dispatch(
        setConfigurationSpecialTextStyle(configuration, style as DraftConfigurationSpecialTextStyle)
      );
    } else {
      dispatch(setConfigurationTextStyle(configuration, style as DraftConfigurationTextStyle));
    }
  }

  const updateAngle = (angle: number) => {
    trackFeature('text-angle');
    draftDispatch(setTextAngleAction(configuration, angle));
  };

  const onEntered = async () => {
    if (text === t('general.defaultText')) {
      const textareaElement = textarea.current;
      if (textareaElement && document.activeElement !== textareaElement) {
        textareaElement.focus();
        textareaElement.select();
        return;
      }
    }

    focusTextPanel();
  };

  return (
    <Fragment>
      <Panel
        hidden={activeSubPanel !== SubPanels.None}
        className="text-panel"
        onEntered={onEntered}
        ref={textPanel}
      >
        {isDraftPlaceholderTextConfiguration(configuration) && (
          <PlaceholderControls
            configuration={configuration}
            onWidthChange={(width: number) =>
              draftDispatch(setPlaceholderWidthAction(configuration, width))
            }
          />
        )}
        <div className="text-panel__text-area">
          <textarea
            ref={textarea}
            name="textarea"
            rows={2}
            value={text}
            onChange={handleInputChange}
            style={{
              textAlign:
                textAlign === 'justify' || !isMultiLine(textContent) ? 'center' : textAlign,
            }}
          />
        </div>
        <TextControls
          configuration={configuration}
          onColorClick={() => setActiveSubPanel(SubPanels.ColorList)}
          onStyleChange={handleStyleChange}
        />
        <FontSelector selectedFont={font} onClick={() => setActiveSubPanel(SubPanels.FontList)} />
        <FontSize configuration={configuration} />
        {!(
          isDraftSpecialTextConfiguration(configuration) ||
          isDraftPlaceholderTextConfiguration(configuration)
        ) && (
          <TextAngle
            textStyle={configuration.text.style}
            onAngleChange={updateAngle}
            disabled={isDraftSpecialTextConfiguration(configuration)}
          />
        )}
        <ConfigurationControls
          configuration={configuration}
          onPaSelectorClick={() => setActiveSubPanel(SubPanels.PrintArea)}
        />
      </Panel>
      <ColorList
        configuration={configuration}
        active={activeSubPanel === SubPanels.ColorList}
        onBackClick={handleBackClick}
      />
      <SubPanel
        active={activeSubPanel === SubPanels.FontList}
        headline={t('configuration.text.selectAFont')}
        parent={t('textSettings.label')}
        className="font-panel"
        onBack={handleBackClick}
      >
        <FontList configuration={configuration} />
      </SubPanel>
      <SubPanel
        active={activeSubPanel === SubPanels.PrintArea}
        headline={t(getPrintAreaLabel())}
        parent={t('textSettings.label')}
        onBack={handleBackClick}
      >
        <PrintAreaSelector configuration={configuration} onClose={handleBackClick} />
      </SubPanel>
    </Fragment>
  );
};
